/**
 * ComponentModePreview — polished miniature SVG mockups of each component mode.
 *
 * Follows the same pattern as HeroModePreview: each mode gets a small
 * illustration rendered inside a 24×16 viewBox that communicates the
 * structural layout at a glance.
 *
 * Photo slots use blue gradients, text bars use currentColor with
 * opacity variation to convey heading/body/button hierarchy.
 */
import React from "react";
import { textColors } from "../../../design-system/tokens";

const IMG_GRADIENT_TOP = "#60a5fa";
const IMG_GRADIENT_BOTTOM = "#1e293b";
const IMG_ACCENT_TOP = "#a78bfa";
const IMG_ACCENT_BOTTOM = "#312e81";

function PhotoGradient({ id, accent = false }) {
  const top = accent ? IMG_ACCENT_TOP : IMG_GRADIENT_TOP;
  const bottom = accent ? IMG_ACCENT_BOTTOM : IMG_GRADIENT_BOTTOM;
  return (
    <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stopColor={top} stopOpacity="0.85" />
      <stop offset="100%" stopColor={bottom} stopOpacity="0.85" />
    </linearGradient>
  );
}

const PREVIEW_RENDERERS = {
  // --- Centered family ---
  centered: (
    <>
      <defs><PhotoGradient id="centered-photo" /></defs>
      {/* Images row on top */}
      <rect x="3" y="1" width="8" height="5" rx="0.6" fill="url(#centered-photo)" />
      <rect x="12" y="1" width="8" height="5" rx="0.6" fill="url(#centered-photo)" opacity="0.7" />
      {/* Title centered */}
      <rect x="6" y="7.5" width="12" height="1.4" rx="0.4" fill="currentColor" opacity="0.9" />
      {/* Body text */}
      <rect x="4" y="9.8" width="16" height="0.8" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="5" y="11.2" width="14" height="0.8" rx="0.3" fill="currentColor" opacity="0.4" />
      {/* Button */}
      <rect x="9" y="13" width="6" height="1.5" rx="0.7" fill="currentColor" opacity="0.6" />
    </>
  ),

  centered_text_only: (
    <>
      {/* Title centered large */}
      <rect x="4" y="2" width="16" height="1.6" rx="0.4" fill="currentColor" opacity="0.9" />
      {/* Body text multiple lines */}
      <rect x="3" y="4.5" width="18" height="0.8" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="4" y="5.9" width="16" height="0.8" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="3.5" y="7.3" width="17" height="0.8" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="4.5" y="8.7" width="15" height="0.8" rx="0.3" fill="currentColor" opacity="0.4" />
      {/* Button */}
      <rect x="8" y="10.5" width="8" height="1.5" rx="0.7" fill="currentColor" opacity="0.6" />
      {/* Small images at bottom */}
      <defs><PhotoGradient id="textonly-photo" /></defs>
      <rect x="6" y="13" width="5" height="2.2" rx="0.4" fill="url(#textonly-photo)" opacity="0.5" />
      <rect x="12" y="13" width="5" height="2.2" rx="0.4" fill="url(#textonly-photo)" opacity="0.4" />
    </>
  ),

  centered_full: (
    <>
      <defs><PhotoGradient id="centfull-photo" /></defs>
      {/* Image fills full width, flush to edges (no rounded corners) */}
      <rect x="0" y="0" width="24" height="8" fill="url(#centfull-photo)" />
      {/* Title centered below */}
      <rect x="6" y="9.5" width="12" height="1.4" rx="0.4" fill="currentColor" opacity="0.9" />
      {/* Body text */}
      <rect x="4" y="11.8" width="16" height="0.8" rx="0.3" fill="currentColor" opacity="0.5" />
      {/* Button */}
      <rect x="9" y="13.5" width="6" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  centered_carousel: (
    <>
      <defs><PhotoGradient id="centcar-photo" /></defs>
      {/* Single image visible with crossfade arrows */}
      <rect x="3" y="1" width="18" height="6" rx="0.6" fill="url(#centcar-photo)" />
      {/* Left/Right arrows to imply cycling */}
      <path d="M1.5 4 L0.5 4 L1.5 3 L1.5 5 Z" fill="currentColor" opacity="0.5" />
      <path d="M22.5 4 L23.5 4 L22.5 3 L22.5 5 Z" fill="currentColor" opacity="0.5" />
      {/* Dots at bottom of image */}
      <circle cx="10.5" cy="6" r="0.4" fill="white" opacity="0.9" />
      <circle cx="12" cy="6" r="0.4" fill="white" opacity="0.5" />
      <circle cx="13.5" cy="6" r="0.4" fill="white" opacity="0.5" />
      {/* Title centered below */}
      <rect x="6" y="8.5" width="12" height="1.4" rx="0.4" fill="currentColor" opacity="0.9" />
      {/* Body text */}
      <rect x="4" y="10.8" width="16" height="0.8" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="5" y="12.2" width="14" height="0.8" rx="0.3" fill="currentColor" opacity="0.4" />
      {/* Button */}
      <rect x="9" y="14" width="6" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  // --- Split family ---
  split_left: (
    <>
      <defs><PhotoGradient id="split-l-photo" /></defs>
      {/* Image on left half */}
      <rect x="0.5" y="0.5" width="11" height="15" rx="0.6" fill="url(#split-l-photo)" />
      {/* Content on right */}
      <rect x="13" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="7.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  split_right: (
    <>
      <defs><PhotoGradient id="split-r-photo" /></defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="7.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Image on right half */}
      <rect x="12.5" y="0.5" width="11" height="15" rx="0.6" fill="url(#split-r-photo)" />
    </>
  ),

  split_left_full: (
    <>
      <defs><PhotoGradient id="splf-l-photo" /></defs>
      {/* Image fills entire left half (no rounded corners, flush to edges) */}
      <rect x="0" y="0" width="12" height="16" fill="url(#splf-l-photo)" />
      {/* Content on right */}
      <rect x="13.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="7.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  split_right_full: (
    <>
      <defs><PhotoGradient id="splf-r-photo" /></defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="7.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Image fills entire right half (no rounded corners, flush to edges) */}
      <rect x="12" y="0" width="12" height="16" fill="url(#splf-r-photo)" />
    </>
  ),

  split_left_carousel: (
    <>
      <defs><PhotoGradient id="splc-l-photo" /></defs>
      {/* Image on left with carousel arrows */}
      <rect x="1" y="1" width="10" height="14" rx="0.6" fill="url(#splc-l-photo)" />
      {/* Dots at bottom of image area */}
      <circle cx="4.5" cy="13.5" r="0.4" fill="white" opacity="0.9" />
      <circle cx="6" cy="13.5" r="0.4" fill="white" opacity="0.5" />
      <circle cx="7.5" cy="13.5" r="0.4" fill="white" opacity="0.5" />
      {/* Content on right */}
      <rect x="13" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="8.5" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  split_right_carousel: (
    <>
      <defs><PhotoGradient id="splc-r-photo" /></defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="8.5" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Image on right with carousel dots */}
      <rect x="13" y="1" width="10" height="14" rx="0.6" fill="url(#splc-r-photo)" />
      {/* Dots at bottom of image area */}
      <circle cx="16.5" cy="13.5" r="0.4" fill="white" opacity="0.9" />
      <circle cx="18" cy="13.5" r="0.4" fill="white" opacity="0.5" />
      <circle cx="19.5" cy="13.5" r="0.4" fill="white" opacity="0.5" />
    </>
  ),

  // --- Alternating family ---
  alternating: (
    <>
      <defs>
        <PhotoGradient id="alt-photo" />
        <PhotoGradient id="alt-photo2" accent />
      </defs>
      {/* Row 1: image left, text right */}
      <rect x="1" y="0.8" width="7" height="5.5" rx="0.5" fill="url(#alt-photo)" />
      <rect x="9.5" y="1.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="9.5" y="3.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="9.5" y="4.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      {/* Row 2: text left, image right */}
      <rect x="1.5" y="8.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="10.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="11.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      <rect x="16" y="8" width="7" height="5.5" rx="0.5" fill="url(#alt-photo2)" />
    </>
  ),

  alternating_right: (
    <>
      <defs>
        <PhotoGradient id="altr-photo" />
        <PhotoGradient id="altr-photo2" accent />
      </defs>
      {/* Row 1: text left, image right */}
      <rect x="1.5" y="1.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="3.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="4.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      <rect x="16" y="0.8" width="7" height="5.5" rx="0.5" fill="url(#altr-photo)" />
      {/* Row 2: image left, text right */}
      <rect x="1" y="8" width="7" height="5.5" rx="0.5" fill="url(#altr-photo2)" />
      <rect x="9.5" y="8.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="9.5" y="10.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="9.5" y="11.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
    </>
  ),

  alternating_full: (
    <>
      <defs>
        <PhotoGradient id="altf-photo" />
        <PhotoGradient id="altf-photo2" accent />
      </defs>
      {/* Row 1: image full-bleed left, text right */}
      <rect x="0" y="0" width="12" height="7.5" fill="url(#altf-photo)" />
      <rect x="13.5" y="1.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="13.5" y="3.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="4.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      {/* Row 2: text left, image full-bleed right */}
      <rect x="1.5" y="9.3" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="11" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="12.2" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      <rect x="12" y="8.5" width="12" height="7.5" fill="url(#altf-photo2)" />
    </>
  ),

  alternating_right_full: (
    <>
      <defs>
        <PhotoGradient id="altrf-photo" />
        <PhotoGradient id="altrf-photo2" accent />
      </defs>
      {/* Row 1: text left, image full-bleed right */}
      <rect x="1.5" y="1.8" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="3.5" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="4.7" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
      <rect x="12" y="0" width="12" height="7.5" fill="url(#altrf-photo)" />
      {/* Row 2: image full-bleed left, text right */}
      <rect x="0" y="8.5" width="12" height="7.5" fill="url(#altrf-photo2)" />
      <rect x="13.5" y="9.3" width="7" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="13.5" y="11" width="6" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="12.2" width="5.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.35" />
    </>
  ),

  alternating_carousel: (
    <>
      <defs>
        <PhotoGradient id="altc-photo" />
        <PhotoGradient id="altc-photo2" accent />
      </defs>
      {/* Row 1: image left with dot indicators, text right */}
      <rect x="1" y="1" width="10" height="6.5" rx="0.6" fill="url(#altc-photo)" />
      <circle cx="4.5" cy="6.5" r="0.35" fill="white" opacity="0.9" />
      <circle cx="5.7" cy="6.5" r="0.35" fill="white" opacity="0.5" />
      <circle cx="6.9" cy="6.5" r="0.35" fill="white" opacity="0.5" />
      <rect x="13" y="2" width="8" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="13" y="3.8" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      {/* Row 2: text left, image right with dot indicators */}
      <rect x="1.5" y="9.5" width="8" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="11.3" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="8.5" width="10" height="6.5" rx="0.6" fill="url(#altc-photo2)" />
      <circle cx="16.5" cy="14" r="0.35" fill="white" opacity="0.9" />
      <circle cx="17.7" cy="14" r="0.35" fill="white" opacity="0.5" />
      <circle cx="18.9" cy="14" r="0.35" fill="white" opacity="0.5" />
    </>
  ),

  alternating_right_carousel: (
    <>
      <defs>
        <PhotoGradient id="altrc-photo" />
        <PhotoGradient id="altrc-photo2" accent />
      </defs>
      {/* Row 1: text left, image right with dots */}
      <rect x="1.5" y="2" width="8" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="1.5" y="3.8" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="1" width="10" height="6.5" rx="0.6" fill="url(#altrc-photo)" />
      <circle cx="16.5" cy="6.5" r="0.35" fill="white" opacity="0.9" />
      <circle cx="17.7" cy="6.5" r="0.35" fill="white" opacity="0.5" />
      <circle cx="18.9" cy="6.5" r="0.35" fill="white" opacity="0.5" />
      {/* Row 2: image left with dots, text right */}
      <rect x="1" y="8.5" width="10" height="6.5" rx="0.6" fill="url(#altrc-photo2)" />
      <circle cx="4.5" cy="14" r="0.35" fill="white" opacity="0.9" />
      <circle cx="5.7" cy="14" r="0.35" fill="white" opacity="0.5" />
      <circle cx="6.9" cy="14" r="0.35" fill="white" opacity="0.5" />
      <rect x="13" y="9.5" width="8" height="1" rx="0.3" fill="currentColor" opacity="0.8" />
      <rect x="13" y="11.3" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
    </>
  ),

  // --- Mosaic family ---
  mosaic: (
    <>
      <defs>
        <PhotoGradient id="mos-photo" />
        <PhotoGradient id="mos-photo2" accent />
      </defs>
      {/* Mosaic collage on left */}
      <rect x="0.5" y="0.5" width="7" height="9" rx="0.5" fill="url(#mos-photo)" />
      <rect x="0.5" y="10" width="3.2" height="5.5" rx="0.4" fill="url(#mos-photo2)" />
      <rect x="4.2" y="10" width="3.3" height="5.5" rx="0.4" fill="url(#mos-photo)" opacity="0.7" />
      <rect x="8" y="0.5" width="3.5" height="5" rx="0.4" fill="url(#mos-photo2)" opacity="0.8" />
      <rect x="8" y="6" width="3.5" height="3.5" rx="0.4" fill="url(#mos-photo)" opacity="0.6" />
      {/* Content on right */}
      <rect x="13" y="2" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13" y="4.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13" y="5.5" width="7.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="13" y="6.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="9" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  mosaic_right: (
    <>
      <defs>
        <PhotoGradient id="mosr-photo" />
        <PhotoGradient id="mosr-photo2" accent />
      </defs>
      {/* Content on left */}
      <rect x="1.5" y="2" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="4.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="5.5" width="7.5" height="0.7" rx="0.3" fill="currentColor" opacity="0.45" />
      <rect x="1.5" y="6.8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="9" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Mosaic collage on right */}
      <rect x="12.5" y="0.5" width="7" height="9" rx="0.5" fill="url(#mosr-photo)" />
      <rect x="12.5" y="10" width="3.2" height="5.5" rx="0.4" fill="url(#mosr-photo2)" />
      <rect x="16.2" y="10" width="3.3" height="5.5" rx="0.4" fill="url(#mosr-photo)" opacity="0.7" />
      <rect x="20" y="0.5" width="3.5" height="5" rx="0.4" fill="url(#mosr-photo2)" opacity="0.8" />
      <rect x="20" y="6" width="3.5" height="3.5" rx="0.4" fill="url(#mosr-photo)" opacity="0.6" />
    </>
  ),

  mosaic_full: (
    <>
      <defs>
        <PhotoGradient id="mosf-photo" />
        <PhotoGradient id="mosf-photo2" accent />
      </defs>
      {/* Mosaic fills entire left half — flush to edges */}
      <rect x="0" y="0" width="8" height="16" fill="url(#mosf-photo)" />
      <rect x="8" y="0" width="4" height="8" fill="url(#mosf-photo2)" opacity="0.8" />
      <rect x="8" y="8" width="4" height="8" fill="url(#mosf-photo)" opacity="0.7" />
      {/* Content on right */}
      <rect x="13.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  mosaic_right_full: (
    <>
      <defs>
        <PhotoGradient id="mosrf-photo" />
        <PhotoGradient id="mosrf-photo2" accent />
      </defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Mosaic fills entire right half — flush to edges */}
      <rect x="12" y="0" width="8" height="16" fill="url(#mosrf-photo)" />
      <rect x="20" y="0" width="4" height="8" fill="url(#mosrf-photo2)" opacity="0.8" />
      <rect x="20" y="8" width="4" height="8" fill="url(#mosrf-photo)" opacity="0.7" />
    </>
  ),

  mosaic_shuffle: (
    <>
      <defs>
        <PhotoGradient id="moss-photo" />
        <PhotoGradient id="moss-photo2" accent />
      </defs>
      {/* Mosaic collage on left with motion arrows */}
      <rect x="1" y="0.5" width="5" height="7" rx="0.5" fill="url(#moss-photo)" />
      <rect x="6.5" y="0.5" width="5" height="3.2" rx="0.4" fill="url(#moss-photo2)" opacity="0.8" />
      <rect x="6.5" y="4.2" width="5" height="3.3" rx="0.4" fill="url(#moss-photo)" opacity="0.7" />
      <rect x="1" y="8" width="5" height="7.5" rx="0.5" fill="url(#moss-photo2)" opacity="0.75" />
      <rect x="6.5" y="8" width="5" height="7.5" rx="0.5" fill="url(#moss-photo)" opacity="0.6" />
      {/* Shuffle motion indicator arrows */}
      <path d="M3.5 10.5 L3.5 12.5 M3 11.5 L3.5 10.5 L4 11.5" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.7" />
      <path d="M9 4 L9 2 M8.5 3 L9 4 L9.5 3" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.7" />
      {/* Content on right */}
      <rect x="13.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  mosaic_right_shuffle: (
    <>
      <defs>
        <PhotoGradient id="mosrs-photo" />
        <PhotoGradient id="mosrs-photo2" accent />
      </defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Mosaic collage on right with motion arrows */}
      <rect x="12.5" y="0.5" width="5" height="7" rx="0.5" fill="url(#mosrs-photo)" />
      <rect x="18" y="0.5" width="5" height="3.2" rx="0.4" fill="url(#mosrs-photo2)" opacity="0.8" />
      <rect x="18" y="4.2" width="5" height="3.3" rx="0.4" fill="url(#mosrs-photo)" opacity="0.7" />
      <rect x="12.5" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosrs-photo2)" opacity="0.75" />
      <rect x="18" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosrs-photo)" opacity="0.6" />
      {/* Shuffle motion indicator arrows */}
      <path d="M15 10.5 L15 12.5 M14.5 11.5 L15 10.5 L15.5 11.5" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.7" />
      <path d="M20.5 4 L20.5 2 M20 3 L20.5 4 L21 3" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.7" />
    </>
  ),

  mosaic_drift: (
    <>
      <defs>
        <PhotoGradient id="mosd-photo" />
        <PhotoGradient id="mosd-photo2" accent />
      </defs>
      {/* Mosaic collage on left with subtle zoom indicators */}
      <rect x="0.5" y="0.5" width="5" height="7" rx="0.5" fill="url(#mosd-photo)" />
      <rect x="6" y="0.5" width="5" height="3.2" rx="0.4" fill="url(#mosd-photo2)" opacity="0.8" />
      <rect x="6" y="4.2" width="5" height="3.3" rx="0.4" fill="url(#mosd-photo)" opacity="0.7" />
      <rect x="0.5" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosd-photo2)" opacity="0.75" />
      <rect x="6" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosd-photo)" opacity="0.6" />
      {/* Zoom/drift indicators (expanding corners) */}
      <path d="M1.5 1.5 L1 1 M5 1.5 L5.5 1" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
      <path d="M7 5 L6.5 4.5 M10.5 5 L11 4.5" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
      {/* Content on right */}
      <rect x="13" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="13" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="13" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="13" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
    </>
  ),

  mosaic_right_drift: (
    <>
      <defs>
        <PhotoGradient id="mosrd-photo" />
        <PhotoGradient id="mosrd-photo2" accent />
      </defs>
      {/* Content on left */}
      <rect x="1.5" y="3" width="9" height="1.3" rx="0.4" fill="currentColor" opacity="0.9" />
      <rect x="1.5" y="5.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.5" />
      <rect x="1.5" y="6.5" width="7" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="8" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="1.5" y="10" width="5" height="1.3" rx="0.6" fill="currentColor" opacity="0.6" />
      {/* Mosaic collage on right with zoom indicators */}
      <rect x="12.5" y="0.5" width="5" height="7" rx="0.5" fill="url(#mosrd-photo)" />
      <rect x="18" y="0.5" width="5" height="3.2" rx="0.4" fill="url(#mosrd-photo2)" opacity="0.8" />
      <rect x="18" y="4.2" width="5" height="3.3" rx="0.4" fill="url(#mosrd-photo)" opacity="0.7" />
      <rect x="12.5" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosrd-photo2)" opacity="0.75" />
      <rect x="18" y="8" width="5" height="7.5" rx="0.5" fill="url(#mosrd-photo)" opacity="0.6" />
      {/* Zoom/drift indicators */}
      <path d="M13.5 1.5 L13 1 M17 1.5 L17.5 1" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
      <path d="M19 5 L18.5 4.5 M22.5 5 L23 4.5" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
    </>
  ),

  // --- Gallery family ---
  gallery: (
    <>
      <defs><PhotoGradient id="gal-photo" /></defs>
      {/* Row of images */}
      <rect x="1" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" />
      <rect x="6.8" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" opacity="0.8" />
      <rect x="12.6" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" opacity="0.7" />
      <rect x="18.4" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" opacity="0.6" />
      {/* Second row */}
      <rect x="1" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" opacity="0.55" />
      <rect x="6.8" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gal-photo)" opacity="0.5" />
      {/* Content below */}
      <rect x="4" y="10.8" width="12" height="1.2" rx="0.4" fill="currentColor" opacity="0.8" />
      <rect x="5" y="12.6" width="10" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="8" y="14" width="6" height="1.2" rx="0.6" fill="currentColor" opacity="0.5" />
    </>
  ),

  gallery_masonry: (
    <>
      <defs>
        <PhotoGradient id="galm-photo" />
        <PhotoGradient id="galm-photo2" accent />
      </defs>
      {/* Masonry-style varying heights */}
      <rect x="1" y="0.8" width="5" height="5.5" rx="0.4" fill="url(#galm-photo)" />
      <rect x="6.6" y="0.8" width="5" height="3.5" rx="0.4" fill="url(#galm-photo2)" opacity="0.8" />
      <rect x="12.2" y="0.8" width="5" height="6.5" rx="0.4" fill="url(#galm-photo)" opacity="0.7" />
      <rect x="17.8" y="0.8" width="5" height="4.5" rx="0.4" fill="url(#galm-photo2)" opacity="0.65" />
      {/* Second row offset */}
      <rect x="1" y="7" width="5" height="3.5" rx="0.4" fill="url(#galm-photo2)" opacity="0.6" />
      <rect x="6.6" y="5" width="5" height="5.5" rx="0.4" fill="url(#galm-photo)" opacity="0.55" />
      <rect x="17.8" y="6" width="5" height="5" rx="0.4" fill="url(#galm-photo)" opacity="0.5" />
      {/* Content below */}
      <rect x="4" y="12.5" width="12" height="1.1" rx="0.4" fill="currentColor" opacity="0.7" />
      <rect x="6" y="14.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
    </>
  ),

  gallery_full: (
    <>
      <defs><PhotoGradient id="galf-photo" /></defs>
      {/* Seamless edge-to-edge grid — no gaps, no rounded corners */}
      <rect x="0" y="0" width="6" height="5" fill="url(#galf-photo)" />
      <rect x="6" y="0" width="6" height="5" fill="url(#galf-photo)" opacity="0.85" />
      <rect x="12" y="0" width="6" height="5" fill="url(#galf-photo)" opacity="0.7" />
      <rect x="18" y="0" width="6" height="5" fill="url(#galf-photo)" opacity="0.6" />
      <rect x="0" y="5" width="6" height="5" fill="url(#galf-photo)" opacity="0.6" />
      <rect x="6" y="5" width="6" height="5" fill="url(#galf-photo)" opacity="0.7" />
      <rect x="12" y="5" width="6" height="5" fill="url(#galf-photo)" opacity="0.85" />
      <rect x="18" y="5" width="6" height="5" fill="url(#galf-photo)" opacity="0.75" />
      {/* Content below */}
      <rect x="4" y="11.5" width="12" height="1.1" rx="0.4" fill="currentColor" opacity="0.7" />
      <rect x="6" y="13.2" width="8" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="8" y="14.5" width="6" height="1.2" rx="0.6" fill="currentColor" opacity="0.5" />
    </>
  ),

  gallery_carousel: (
    <>
      <defs><PhotoGradient id="galc-photo" /></defs>
      {/* Grid of images (one page visible) */}
      <rect x="1" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" />
      <rect x="6.8" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" opacity="0.8" />
      <rect x="12.6" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" opacity="0.7" />
      <rect x="18.4" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" opacity="0.6" />
      <rect x="1" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" opacity="0.6" />
      <rect x="6.8" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#galc-photo)" opacity="0.7" />
      {/* Page dots below grid */}
      <circle cx="10" cy="11" r="0.5" fill="currentColor" opacity="0.8" />
      <circle cx="12" cy="11" r="0.5" fill="currentColor" opacity="0.35" />
      <circle cx="14" cy="11" r="0.5" fill="currentColor" opacity="0.35" />
      {/* Content below */}
      <rect x="5" y="12.5" width="14" height="1" rx="0.4" fill="currentColor" opacity="0.7" />
      <rect x="7" y="14.2" width="10" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
    </>
  ),

  gallery_drift: (
    <>
      <defs><PhotoGradient id="gald-photo" /></defs>
      {/* Grid of images with zoom indicators */}
      <rect x="1" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" />
      <rect x="6.8" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.8" />
      <rect x="12.6" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.7" />
      <rect x="18.4" y="0.8" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.6" />
      <rect x="1" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.65" />
      <rect x="6.8" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.75" />
      <rect x="12.6" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.55" />
      <rect x="18.4" y="5.4" width="5.2" height="4" rx="0.4" fill="url(#gald-photo)" opacity="0.6" />
      {/* Zoom/drift indicators on corners */}
      <path d="M1.8 1.5 L1.3 1 M5.5 1.5 L6 1" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
      <path d="M7.6 6 L7.1 5.6 M11.3 6 L11.8 5.6" stroke="currentColor" strokeWidth="0.3" fill="none" opacity="0.6" />
      {/* Content below */}
      <rect x="5" y="10.8" width="14" height="1" rx="0.4" fill="currentColor" opacity="0.7" />
      <rect x="7" y="12.5" width="10" height="0.7" rx="0.3" fill="currentColor" opacity="0.4" />
      <rect x="8" y="14" width="6" height="1.2" rx="0.6" fill="currentColor" opacity="0.5" />
    </>
  ),
};

export default function ComponentModePreview({ modeId, isSelected }) {
  const renderer = PREVIEW_RENDERERS[modeId];

  if (!renderer) {
    const letter = (modeId || "?")[0].toUpperCase();
    return (
      <div
        className={`w-full h-full flex items-center justify-center ${
          isSelected ? "text-white/80" : textColors.muted
        }`}
      >
        <span className="text-lg font-bold">{letter}</span>
      </div>
    );
  }

  return (
    <svg
      viewBox="0 0 24 16"
      className={`w-full h-full ${isSelected ? "text-white" : textColors.primary}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      {renderer}
    </svg>
  );
}
